/**
 * DataStatusDashboard - Dashboard de Status dos Dados
 * 
 * Mostra ao usuário (e admin) o status de atualização de cada dado
 * 
 * PRINCÍPIO: Transparência total - usuário sempre sabe se o dado está fresco
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useTranslation } from '../../translations';
import { 
  DATA_FRESHNESS_CONFIG, 
  calculateFreshness, 
  getFreshnessIndicator,
  fetchCambioAutomatico,
  getNextUpdateDates,
  getUpdateCalendar 
} from './DataFreshness';
import AdminPanel from './AdminPanel';

const DataStatusDashboard = ({ quotations, onRefresh }) => {
  const { t } = useTranslation();
  const [showAdmin, setShowAdmin] = useState(false);
  const [autoUpdating, setAutoUpdating] = useState(false);
  const [lastAutoUpdate, setLastAutoUpdate] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  // Calcular status de cada dado
  const getDataStatus = () => {
    const status = [];
    
    Object.keys(DATA_FRESHNESS_CONFIG).forEach(key => {
      const config = DATA_FRESHNESS_CONFIG[key];
      const quotation = quotations[key];
      
      if (quotation) {
        const freshness = calculateFreshness(quotation.reference_date, config.maxAgeDays);
        const indicator = getFreshnessIndicator(freshness.status);
        
        status.push({
          key,
          name: config.name,
          value: quotation.value,
          unit: quotation.unit,
          referenceDate: quotation.reference_date,
          source: config.source,
          updateFrequency: config.updateFrequency,
          canAutoUpdate: config.canAutoUpdate,
          checkUrl: config.checkUrl,
          freshness,
          indicator,
        });
      }
    });
    
    return status;
  };

  const dataStatus = getDataStatus();
  
  // Contar status
  const freshCount = dataStatus.filter(d => d.freshness.status === 'fresh').length;
  const warningCount = dataStatus.filter(d => d.freshness.status === 'warning').length;
  const staleCount = dataStatus.filter(d => d.freshness.status === 'stale').length;
  const needsAttention = warningCount + staleCount;

  // Atualizar câmbio automaticamente
  const handleAutoUpdateCambio = async () => {
    setAutoUpdating(true);
    try {
      const cambioData = await fetchCambioAutomatico();
      if (cambioData && onRefresh) {
        // Aqui você chamaria a API para atualizar o câmbio
        console.log('Câmbio atualizado:', cambioData);
        setLastAutoUpdate(new Date().toLocaleTimeString('pt-BR'));
        // Chamar API de atualização
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        await fetch(`${API_URL}/api/market/quotations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'cambio',
            value: cambioData.value,
            unit: 'R$/US$',
            source: cambioData.source,
            reference_date: cambioData.date,
          })
        });
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      console.error('Erro ao atualizar câmbio:', error);
    } finally {
      setAutoUpdating(false);
    }
  };

  // Atualizar tudo via Radar (admin)
  const [radarRunning, setRadarRunning] = useState(false);
  const handleRadarRun = async () => {
    setRadarRunning(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${API_URL}/api/admin/radar/run`);
      toast.success('🛰️ Radar disparado! Dólar, TSR-20, GEB-10 estimado e participação atualizam em ~1 min. Coágulo e GEB-10 APABOR entram na fila de aprovação se houver valor novo.');
      setTimeout(() => {
        if (onRefresh) onRefresh();
        setRadarRunning(false);
      }, 70000);
    } catch (e) {
      setRadarRunning(false);
      if (e.response?.status === 401 || e.response?.status === 403) {
        toast.error('Apenas administradores podem disparar o Radar.');
      } else {
        toast.error('Erro ao disparar o Radar. Tente novamente.');
      }
    }
  };

  // Próximas atualizações
  const nextUpdates = getNextUpdateDates();

  return (
    <div className="space-y-4">
      {/* Admin Panel Modal */}
      {showAdmin && (
        <AdminPanel 
          onClose={() => setShowAdmin(false)} 
          onUpdate={onRefresh}
        />
      )}

      {/* Header com resumo */}
      <Card className={`border-2 ${needsAttention > 0 ? 'border-amber-400 bg-amber-50' : 'border-green-400 bg-green-50'}`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-gray-800 flex items-center gap-2">
              <span className="text-xl">📊</span>
              Status dos Dados
              <button
                onClick={() => setShowHelp(!showHelp)}
                className="ml-2 text-blue-600 hover:text-blue-800 text-sm font-normal"
                title="Como funciona?"
              >
                ❓
              </button>
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              {localStorage.getItem('token') && (
                <button
                  onClick={handleRadarRun}
                  disabled={radarRunning}
                  data-testid="status-radar-run-btn"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-60"
                >
                  {radarRunning ? '⏳ Radar rodando…' : '🛰️ Atualizar via Radar'}
                </button>
              )}
              <button
                onClick={() => setShowAdmin(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                ⚙️ Atualizar Dados
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Tooltip de Ajuda - ATUALIZADO */}
          {showHelp && (
            <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-bold text-blue-800 flex items-center gap-2">
                  💡 Como Funciona a Atualização de Dados
                </h4>
                <button onClick={() => setShowHelp(false)} className="text-blue-600 hover:text-blue-800">✕</button>
              </div>
              
              <div className="space-y-3 text-sm">
                {/* Dados Obrigatórios */}
                <div className="bg-white p-3 rounded-lg border border-red-200">
                  <p className="font-medium text-red-700 mb-2">🔴 Atualizações OBRIGATÓRIAS (para cálculos):</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-gray-600">
                    <div className="flex items-center gap-2">
                      <span>🏭</span>
                      <span><strong>GEB-10 APABOR</strong> - Bimestral (6x/ano)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span>🌿</span>
                      <span><strong>{t('maImportPrice')}</strong> - {t('qpMonthlyUpdate')} (~12x/ano)</span>
                    </div>
                  </div>
                </div>
                
                {/* Automático */}
                <div className="bg-white p-3 rounded-lg border border-green-200">
                  <p className="font-medium text-green-700 mb-2">🟢 Atualização AUTOMÁTICA:</p>
                  <div className="flex items-center gap-2 text-gray-600">
                    <span>💱</span>
                    <span><strong>{t('qpDollarBCB')}</strong> - Clique no botão "🔄 {t('qpUpdate')} Câmbio"</span>
                  </div>
                </div>

                {/* Opcional */}
                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <p className="font-medium text-gray-600 mb-2">🟡 Opcionais (contexto):</p>
                  <div className="text-gray-500 text-xs">
                    Internacional, Custo Produção - atualizar quando disponível
                  </div>
                </div>

                {/* Como atualizar */}
                <div className="bg-white p-3 rounded-lg border">
                  <p className="font-medium text-gray-800 mb-1">📝 Como atualizar manualmente:</p>
                  <ol className="text-gray-600 space-y-1 ml-4 list-decimal">
                    <li>Clique em <strong>"⚙️ Atualizar Dados"</strong></li>
                    <li>Use o link da <strong>fonte oficial</strong> para consultar o novo valor</li>
                    <li>Digite o valor e clique <strong>"💾 Salvar"</strong></li>
                  </ol>
                </div>

                {/* Calendário resumido */}
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <p className="font-medium text-amber-800 mb-1">📅 Calendário de Publicações:</p>
                  <div className="text-amber-700 text-xs grid grid-cols-2 gap-1">
                    <span>• GEB-10: <strong>Início do bimestre</strong></span>
                    <span>• Coágulo: <strong>Dias 15-20/mês</strong></span>
                    <span>• Dólar: <strong>Diário (auto)</strong></span>
                    <span>• Custo: <strong>Anual</strong></span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Resumo visual */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-lg">🟢</span>
              <span className="font-medium">{freshCount}</span>
              <span className="text-sm text-gray-600">período vigente</span>
            </div>
            {warningCount > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-lg">🟡</span>
                <span className="font-medium">{warningCount}</span>
                <span className="text-sm text-gray-600">verificar período</span>
              </div>
            )}
            {staleCount > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-lg">🟠</span>
                <span className="font-medium">{staleCount}</span>
                <span className="text-sm text-gray-600">período anterior</span>
              </div>
            )}
          </div>

          {/* Informativo sobre dados de período anterior */}
          {needsAttention > 0 && (
            <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-lg mb-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl">ℹ️</span>
                <div>
                  <p className="text-blue-800 font-medium">
                    Sobre os dados de período anterior:
                  </p>
                  <p className="text-blue-700 text-sm mt-1">
                    <strong>Os valores continuam válidos como referência</strong> até a publicação do novo período pelas fontes oficiais (IEA, APABOR, etc.).
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Botão de atualização automática do câmbio */}
          <div className="flex items-center gap-4">
            <button
              onClick={handleAutoUpdateCambio}
              disabled={autoUpdating}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 transition-colors text-sm"
            >
              {autoUpdating ? '⏳ Atualizando...' : '🔄 Atualizar Câmbio (Auto)'}
            </button>
            {lastAutoUpdate && (
              <span className="text-xs text-gray-500">
                Última atualização automática: {lastAutoUpdate}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Lista detalhada de status */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-gray-700">📋 Detalhe por Dado</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {dataStatus.map((item) => (
              <div 
                key={item.key}
                className={`p-3 rounded-lg border flex items-center justify-between ${item.indicator.bgColor}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{item.indicator.emoji}</span>
                  <div>
                    <div className="font-medium text-gray-800">{item.name}</div>
                    <div className="text-sm text-gray-600">
                      R$ {item.value?.toFixed(2).replace('.', ',')} • {item.referenceDate} • {item.source}
                    </div>
                    {item.freshness.status === 'stale' && (
                      <div className="text-xs text-blue-600 mt-1">
                        ✓ Válido como referência até novo período
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-medium ${item.indicator.color}`}>
                    {item.indicator.label}
                  </div>
                  <div className="text-xs text-gray-500">
                    Validade: {item.updateFrequency}
                  </div>
                  {item.checkUrl && item.freshness.status !== 'fresh' && (
                    <a 
                      href={item.checkUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Verificar novo período →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Próximas atualizações esperadas */}
      <Card className="border-blue-200">
        <CardHeader className="pb-2 bg-blue-50">
          <CardTitle className="text-lg text-blue-800">📅 Calendário de Publicações</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <p className="text-sm text-gray-600 mb-4">
            Quando cada fonte oficial publica novos dados:
          </p>
          
          {/* Próximas publicações */}
          <div className="space-y-3 mb-4">
            {nextUpdates.map((update, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg border hover:border-blue-300 transition-colors">
                <div className="flex items-center gap-3">
                  <span className="text-xl">{update.canAutoUpdate ? '🤖' : '📋'}</span>
                  <div>
                    <div className="font-medium text-gray-800">{update.name}</div>
                    <div className="text-xs text-gray-500">{update.source} • {update.frequency}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-blue-700">{update.dateLabel}</div>
                  {update.note && (
                    <div className="text-xs text-gray-500">{update.note}</div>
                  )}
                  {update.checkUrl && (
                    <a 
                      href={update.checkUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Verificar →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Legenda */}
          <div className="flex items-center gap-4 text-xs text-gray-500 border-t pt-3">
            <span className="flex items-center gap-1">🤖 Atualização automática</span>
            <span className="flex items-center gap-1">📋 Atualização manual</span>
          </div>

          {/* Resumo do calendário */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-700 mb-2 text-sm">📆 Resumo do Calendário</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('qpDollarBCB')}:</span>
                <span className="text-gray-800">Diário (dias úteis)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('qpInternationalSGX')}:</span>
                <span className="text-gray-800">Diário (dias úteis)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">IEA/Coágulo:</span>
                <span className="text-gray-800">Mensal (dias 15-20)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">GEB-10 APABOR:</span>
                <span className="text-gray-800">Bimestral (início do bimestre)</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tripé */}
      <div className="text-center text-xs text-gray-500 py-2">
        ⚖️ TRANSPARÊNCIA: Validade dos dados sempre visível | 
        🔒 SEGURANÇA: Decisões com dados corretos | 
        ✨ SIMPLICIDADE: Status visual claro
      </div>
    </div>
  );
};

export default DataStatusDashboard;
